import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, PaperPlaneTilt, Bug, Lightbulb } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [type, setType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/feedback', { type, message, email: email || null });
      setSubmitted(true);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="feedback-page">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={20} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8">
          {submitted ? (
            <div className="text-center py-8" data-testid="feedback-success">
              <PaperPlaneTilt size={48} weight="light" className="text-[#D4AF37] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-white font-['Outfit'] mb-2">Thank you!</h2>
              <p className="text-[#9EABCE]">Your feedback has been submitted successfully.</p>
              <button onClick={() => navigate(-1)} className="btn-gold px-6 py-2.5 rounded-xl text-sm font-bold mt-6">
                Go Back
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-white font-['Outfit'] mb-2">Support & Feedback</h2>
              <p className="text-[#9EABCE] text-sm mb-6">Report bugs or suggest improvements</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-[#9EABCE] text-sm mb-2 block">Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="feedback-type" className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B112B] border-white/10">
                      <SelectItem value="bug" className="text-white">
                        <span className="flex items-center gap-2"><Bug size={16} /> Bug Report</span>
                      </SelectItem>
                      <SelectItem value="suggestion" className="text-white">
                        <span className="flex items-center gap-2"><Lightbulb size={16} /> Suggestion</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#9EABCE] text-sm mb-2 block">Email (optional)</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="feedback-email"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-[#9EABCE] text-sm mb-2 block">Message</Label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the issue or your suggestion..."
                    required
                    rows={5}
                    data-testid="feedback-message"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  data-testid="feedback-submit-btn"
                  className="w-full btn-gold py-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Submitting...' : 'Submit Feedback'}
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
